import { createSelector, createSlice } from "@reduxjs/toolkit";
import { sortDataBy } from "../../../SharedModule/utils/dataSort";
import { createChangeOrderReducer } from "../../../SharedModule/utils/changeOrderReducer";
import {
  ClientContact,
  ClientKeyContact,
  ClientsKeyContactDetail,
} from "../../types/clients-module-types";
import { EMPTY_NEW_KEY_CONTACT } from "../../utils/clientsUtils";

type ClientKeyContactsState = {
  isLoadingKeyContacts: boolean;
  isLoadedKeyContacts: boolean;
  actualRelationshipReviewId: number | null;
  actualRelationshipReviewName: string | null;
  actualCompanyOwnerUserId: string | null;
  actualRelationshipReviewStatus: boolean | null;
  keyContactsList: ClientKeyContact[];
  orderedBy: {
    orderBy: string;
    criteria: "asc" | "desc";
    dataType?: "string" | "date";
  };
  contact: ClientContact | null;
  actualKeyContactData: ClientsKeyContactDetail;
  isLoadingActualKeyContactData: boolean;
  needUpdateKeyContactList: boolean;
};

export const initialState: ClientKeyContactsState = {
  isLoadingKeyContacts: true,
  isLoadedKeyContacts: false,
  actualRelationshipReviewId: null,
  actualRelationshipReviewName: null,
  actualCompanyOwnerUserId: null,
  actualRelationshipReviewStatus: null,
  keyContactsList: [],
  orderedBy: {
    orderBy: "lastName",
    criteria: "asc",
    dataType: "string",
  },
  contact: null,
  actualKeyContactData: EMPTY_NEW_KEY_CONTACT,
  isLoadingActualKeyContactData: true,
  needUpdateKeyContactList: false,
};

export const clientKeyContactsSlice = createSlice({
  name: "clientKeyContacts",
  initialState,
  reducers: {
    loadKeyContacts: (state, action) => {
      state.keyContactsList = action.payload.data.keyContacts;
      state.actualRelationshipReviewName =
        action.payload.data.relationshipReviewName;
      state.actualCompanyOwnerUserId =
        action.payload.data.companyOwnerUserId;
      state.actualRelationshipReviewStatus = action.payload.data.isActive;
      state.isLoadingKeyContacts = false;
      state.isLoadedKeyContacts = true;
      state.needUpdateKeyContactList = false;
    },
    setActualRelationshipReviewId: (state, action) => {
      state.actualRelationshipReviewId = action.payload;
    },
    setIsLoadingKeyContacts: (state, action) => {
      state.isLoadingKeyContacts = action.payload;
    },
    setNeedUpdateKeyContactList: (state, action) => {
      state.needUpdateKeyContactList = action.payload;
    },
    changeOrder: createChangeOrderReducer("orderedBy"),
    setContact: (state, action) => {
      state.contact = action.payload.contact;
    },
    setActualKeyContactData: (state, action) => {
      state.actualKeyContactData = action.payload;
      state.isLoadingActualKeyContactData = false;
    },
    setIsLoadingActualKeyContactData: (state, action) => {
      state.isLoadingActualKeyContactData = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  loadKeyContacts,
  setActualRelationshipReviewId,
  setIsLoadingKeyContacts,
  changeOrder,
  setNeedUpdateKeyContactList,
  setContact,
  setActualKeyContactData,
  setIsLoadingActualKeyContactData,
} = clientKeyContactsSlice.actions;

export const IsLoadingKeyContacts = (state: any) => {
  return state.clientKeyContacts.isLoadingKeyContacts;
};

export const IsLoadedKeyContacts = (state: any) => {
  return state.clientKeyContacts.isLoadedKeyContacts;
};

export const SelectNeedUpdateKeyContactList = (state: any) => {
  return state.clientKeyContacts.needUpdateKeyContactList;
};

export const SelectIsLoadingActualKeyContactData = (state: any) => {
  return state.clientKeyContacts.isLoadingActualKeyContactData;
};

export const SelectActualRelationshipReviewId = (state: any) => {
  return state.clientKeyContacts.actualRelationshipReviewId;
};

export const SelectActualRelationshipReviewName = (state: any) => {
  return state.clientKeyContacts.actualRelationshipReviewName;
};

export const SelectActualCompanyOwnerUserId = (state: any) => {
  return state.clientKeyContacts.actualCompanyOwnerUserId;
};

export const SelectContact = (state: any) => {
  return state.clientKeyContacts.contact;
};

export const SelectActualKeyContactData = (state: any) => {
  return state.clientKeyContacts.actualKeyContactData;
};

export const SelectActualRelationshipReviewStatus = (state: any) => {
  return state.clientKeyContacts.actualRelationshipReviewStatus;
};

export const SelectClientKeyContacts = (state: any) => {
  return state.clientKeyContacts.keyContactsList;
};

export const SelectKeyContacts = ({
  clientKeyContacts,
}: {
  clientKeyContacts: ClientKeyContactsState;
}) => {
  return { list: clientKeyContacts.keyContactsList };
};

export const SelectOrderCriteria = ({
  clientKeyContacts,
}: {
  clientKeyContacts: ClientKeyContactsState;
}) => clientKeyContacts.orderedBy;

export const SelectOrderedByContacts = createSelector(
  [SelectKeyContacts, SelectOrderCriteria],
  (clientKeyContacts, orderCriteria) => {
    return {
      list:
        (clientKeyContacts.list &&
          sortDataBy(
            clientKeyContacts.list,
            orderCriteria.orderBy,
            orderCriteria.criteria,
            orderCriteria.dataType
          )) ||
        null,
    };
  }
);

export default clientKeyContactsSlice.reducer;
